/**
 * BLE UART bridge profiles — wake bytes and handshake differ by firmware (NUS vs ISSC vs vendor).
 */

export const ISSC_SERVICE_UUID = '49535343-fe7d-4ae5-8fa9-9fafd205e455';
/** ISSC Transparent RX — central writes ESC/POS here (host → printer). */
export const ISSC_WRITE_CHAR_UUID = '49535343-8841-43f4-a8d4-ecbe34729bb3';
/** ISSC Transparent TX — printer → host notify (enable for bridge open on some firmware). */
export const ISSC_NOTIFY_CHAR_UUID = '49535343-1e4d-4bd9-ba61-23c647249616';
/** ISSC Air Patch — some Printer001 firmware needs notify/open before UART forwards data. */
export const ISSC_AIR_PATCH_CHAR_UUID = '49535343-aca3-481c-91ec-d85e28a60318';

export type BleBridgeProfile = 'nus' | 'issc' | 'vendor' | 'generic';

function uuidLower(s: string): string {
  return s.trim().toLowerCase();
}

export function resolveBleBridgeProfile(
  serviceUuid: string,
  characteristicUuid?: string,
): BleBridgeProfile {
  const su = uuidLower(serviceUuid);
  const cu = characteristicUuid ? uuidLower(characteristicUuid) : '';

  if (su.includes('49535343-fe7d')) {
    return 'issc';
  }
  if (su.includes('6e400001')) {
    return 'nus';
  }
  if (
    su.includes('ffe0') ||
    su.includes('ae30') ||
    su.includes('ff00') ||
    su.includes('fff0') ||
    cu.includes('ffe1') ||
    cu.includes('ae01') ||
    cu.includes('ff02')
  ) {
    return 'vendor';
  }
  return 'generic';
}

/** XP-P203A null wake applies to Nordic / vendor UART bridges, not ISSC transparent UART. */
export function shouldPrependXpP203aWake(profile: BleBridgeProfile): boolean {
  return profile === 'nus' || profile === 'vendor';
}

export function isIsscServiceUuid(serviceUuid: string): boolean {
  return resolveBleBridgeProfile(serviceUuid) === 'issc';
}

export function isIsscWriteCharacteristic(serviceUuid: string, characteristicUuid: string): boolean {
  const su = uuidLower(serviceUuid);
  const cu = uuidLower(characteristicUuid);
  return su.includes('49535343-fe7d') && cu.includes('8841');
}

/** ISSC notify-only path — must not be used for ESC/POS writes. */
export function isIsscNotifyRxCharacteristic(serviceUuid: string, characteristicUuid: string): boolean {
  const su = uuidLower(serviceUuid);
  const cu = uuidLower(characteristicUuid);
  return su.includes('49535343-fe7d') && cu.includes('1e4d');
}
